<template>
  <div class="passbook-page">
    <div class="container">

      <section class="hero">
        <button type="button" class="hero-info-btn" @click="rulesOpen = true" aria-label="減碳估算規則">
          <Icon name="circle-information" size="20" />
        </button>
        <div class="hero-label fs-14px">累計減碳</div>
        <div class="hero-balance">
          <span class="num fw-bold">{{ fmt(totalBalance) }}</span>
          <span class="unit fw-bold">kg CO₂e</span>
        </div>
        <div class="hero-equiv fs-12px" v-if="treesPerYear > 0">
          ≈ 種 {{ treesPerYear.toLocaleString() }} 棵相思樹一年所吸收的碳
        </div>
        <div class="hero-month" :class="thisMonthDelta >= 0 ? 'pos' : 'neg'">
          <span class="ind">{{ thisMonthDelta > 0 ? '↑' : 0 > thisMonthDelta ? '↓' : '—' }}</span>
          <span>本月 {{ thisMonthDelta > 0 ? '+' : '' }}{{ fmt(thisMonthDelta) }} kg</span>
        </div>
        <p class="hero-desc fs-12px">{{ balanceDesc }}</p>
      </section>

      <section class="status-section">
        <h3 class="section-title fs-16px fw-bold">永續行動</h3>
        <div class="stat-grid">
          <div class="stat-card">
            <div class="stat-title fs-14px">二手購入率</div>
            <div class="stat-value">
              <span class="num">{{ secondHandRate }}</span><span class="unit">%</span>
            </div>
            <div class="stat-sub fs-12px" v-if="totalWithStatus > 0">
              {{ secondHandCount }} / {{ totalWithStatus }} 件已標
            </div>
            <div class="stat-sub fs-12px" v-else>尚未填寫購入狀態</div>
          </div>

          <div class="stat-card">
            <div class="stat-title fs-14px">材質了解度</div>
            <div class="stat-value">
              <span class="num">{{ materialKnownRate }}</span><span class="unit">%</span>
            </div>
            <div class="stat-sub fs-12px" v-if="inUseCloths.length > 0">
              {{ totalWithMaterial }} / {{ inUseCloths.length }} 件已記錄
            </div>
            <div class="stat-sub fs-12px" v-else>衣櫥裡尚未有單品</div>
          </div>

          <div class="stat-card">
            <div class="stat-title fs-14px">平均穿搭次數</div>
            <div class="stat-value">
              <span class="num">{{ avgWearCount.toFixed(1) }}</span><span class="unit">次</span>
            </div>
            <div class="stat-sub fs-12px">使用中單品平均</div>
          </div>
        </div>
      </section>

      <section class="ledger" v-if="monthlyEntries.length > 0">
        <div class="section-head">
          <h3 class="section-title fs-16px fw-bold">存摺紀錄</h3>
          <span class="section-meta fs-12px">點開可看當月明細</span>
        </div>
        <div class="ledger-list">
          <div
            v-for="entry in displayedEntries"
            :key="entry.key"
            class="entry"
            :class="{ open: !!expanded[entry.key] }"
          >
            <button type="button" class="entry-head" @click="toggle(entry.key)">
              <div class="entry-month">
                <div class="ym-yr fs-12px">{{ entry.year }}</div>
                <div class="ym-mo fs-h3 fw-bold">{{ pad(entry.month) }}月</div>
              </div>
              <div class="entry-stats">
                <div class="stat-line">
                  <span class="stat-label fs-12px">本月</span>
                  <span class="stat-delta fs-14px fw-bold" :class="entry.delta >= 0 ? 'pos' : 'neg'">
                    {{ entry.delta >= 0 ? '+' : '' }}{{ fmt(entry.delta) }}
                  </span>
                  <span class="stat-unit fs-12px">kg</span>
                </div>
                <div class="stat-line">
                  <span class="stat-label fs-12px">餘額</span>
                  <span class="stat-balance fs-14px fw-bold">{{ fmt(entry.balance) }}</span>
                  <span class="stat-unit fs-12px">kg</span>
                </div>
              </div>
              <span class="chev" :class="{ 'is-open': !!expanded[entry.key] }">
                <Icon name="chevron-down" size="24" />
              </span>
            </button>

            <div class="entry-detail" v-if="expanded[entry.key]">
              <div class="detail-row" v-for="b in entry.breakdown" :key="b.type">
                <span class="b-dot" :style="{ backgroundColor: b.color }"></span>
                <span class="b-label fs-14px">{{ b.label }}</span>
                <span class="b-count fs-12px">× {{ b.count }}</span>
                <span class="b-delta fs-14px fw-bold" :class="b.delta >= 0 ? 'pos' : 'neg'">
                  {{ b.delta >= 0 ? '+' : '' }}{{ fmt(b.delta) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <button v-if="canShowMore" class="show-more fs-14px" @click="showMore">
          顯示更多
        </button>
      </section>

      <section class="empty" v-else>
        <p class="fs-14px">
          還沒有任何永續紀錄。試著記錄入手的二手衣、標記環境友善材質，或妥善處理不再穿的單品，減碳量就會開始累積。
        </p>
      </section>

    </div>

    <b-modal
      v-model="rulesOpen"
      centered
      size="md"
      scrollable
      content-class="custom-modal rules-modal"
      hide-footer
    >
      <template #modal-header="{ close }">
        <h5 class="modal-title">減碳估算規則</h5>
        <button type="button" class="modal-close-btn" @click="close" aria-label="關閉">
          <Icon name="close" size="24" />
        </button>
      </template>
      <div class="rules-modal-body">
        <div class="rule-row" v-for="r in rules" :key="r.type">
          <span class="r-dot" :style="{ backgroundColor: r.color }"></span>
          <span class="r-label fs-14px">{{ r.label }}</span>
          <span class="r-delta fs-14px fw-bold" :class="r.delta >= 0 ? 'pos' : 'neg'">
            {{ r.delta >= 0 ? '+' : '' }}{{ fmt(r.delta) }} kg/{{ r.perUnit }}
          </span>
        </div>
        <p class="rule-note fs-12px">
          製造端碳排參考
          <a href="https://impactco2.fr/outils/habillement" target="_blank" rel="noopener">ADEME Impact CO₂ 服飾資料</a>
          （T 恤 6 kg、襯衫 13 kg、牛仔褲 25 kg 等）與
          ADEME 委託 Refashion 2022 二手衣研究估算，
          依日常衣物 mix 推估「平均單品製造碳排約 10–15 kg CO₂e」為基準。
          捐贈／二手販售分數依
          <a href="https://e-info.org.tw/node/237394" target="_blank" rel="noopener">環境部 2022 年舊衣回收統計</a>
          （全台 8.2 萬公噸、約 35% 進入焚化）因應台灣回收實況向下調整。
          實際碳排會隨材質、產地與製程差異，本數值僅供參考，不作為正式碳權計算。
        </p>
        <p class="rule-note fs-12px">
          入手分數依「入手年月」記入，未填則用建檔時間；穿搭依當日穿搭日期記入；回收／販售／捐贈／丟棄依淘汰時間記入，沒記日期則不計分。
        </p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import moment from 'moment';
import Icon from '@/components/commons/Icon.vue';

// kg CO₂e per event，依 ADEME Impact CO₂ + ADEME 委託 Refashion 2022 二手衣研究推估，
// 並以環境部 2022 舊衣回收統計做台灣在地調整
// 基準：依日常衣物 mix 推估，平均單品製造階段碳排約 10–15 kg CO₂e（取中位 ~12 kg）
const POINTS = {
  secondhand: 10,    // 85% × ~12 kg ≈ 10.2（買方避免新製造，Refashion 2022/ADEME）
  eco: 2,            // 再生／低衝擊纖維約省 15–30%（Higg MSI 對齊範圍）
  wear: 0.4,         // ~12 kg / 平均 30 次穿著 ≈ 0.4 kg/次
  donate: 6,         // 歐洲基準 +10，台灣再利用率較低（環境部 2022：~35% 舊衣最終焚化）
  resell: 6,         // 同 donate
  recycle: 2,        // 機械回收可取代部分原料
  trash: -1.5,       // 焚化／掩埋直接排放
};

// 林業及自然保育署：1 棵樹每年約吸 5–10 kg CO₂，相思樹為台灣原生樹中固碳能力最強樹種，採上限 10 kg
const KG_PER_TREE_YEAR = 10;

const RULE_DEFS = [
  { type: 'secondhand', label: '入手二手衣',   delta: POINTS.secondhand, color: '#7DC4B7', perUnit: '件' },
  // 待長腳衣櫥定義「環境友善材質」後再開放，定義完移除此註解即可
  // { type: 'eco',        label: '環境友善材質', delta: POINTS.eco,         color: '#AAD272', perUnit: '件' },
  { type: 'wear',       label: '單品穿著',     delta: POINTS.wear,        color: '#6BB6FF', perUnit: '次' },
  { type: 'donate',     label: '捐贈',         delta: POINTS.donate,      color: '#F2C31C', perUnit: '件' },
  { type: 'resell',     label: '二手販售',     delta: POINTS.resell,      color: '#F09148', perUnit: '件' },
  { type: 'recycle',    label: '舊衣回收',     delta: POINTS.recycle,     color: '#88C57F', perUnit: '件' },
  { type: 'trash',      label: '丟棄處理',     delta: POINTS.trash,       color: '#DE6167', perUnit: '件' },
];

const RULE_BY_TYPE = RULE_DEFS.reduce((m, r) => { m[r.type] = r; return m; }, {});

export default {
  name: 'AnalyzeHealthIndex',
  components: {
    Icon,
  },
  data() {
    return {
      expanded: {},
      displayCount: 3,
      pageSize: 3,
      rulesOpen: false,
      rules: RULE_DEFS,
    };
  },
  async mounted() {
    if (!this.outfits || this.outfits.length === 0) {
      try {
        await this.$store.dispatch('api/readOutfitListPromise');
      } catch (e) {
        // 忽略，存摺仍可用 cloths 算出大部分事件
      }
    }
  },
  computed: {
    ...mapState(['outfits']),
    ...mapGetters(['clothWithWearCount']),

    cloths() {
      return this.clothWithWearCount || [];
    },

    events() {
      const list = [];

      const acquireYM = (c) => {
        if (
          typeof c.getYear === 'number' && typeof c.getMonth === 'number' &&
          c.getYear !== -1 && c.getMonth !== -1 &&
          !isNaN(c.getYear) && !isNaN(c.getMonth)
        ) {
          return { year: c.getYear, month: c.getMonth };
        }
        if (c.create_at) {
          const m = moment(c.create_at);
          if (m.isValid()) return { year: m.year(), month: m.month() + 1 };
        }
        return null;
      };

      const parseYM = (val) => {
        if (!val) return null;
        const m = moment(val);
        if (!m.isValid()) return null;
        return { year: m.year(), month: m.month() + 1 };
      };

      const recycleTypeMap = { 0: 'donate', 1: 'resell', 2: 'recycle', 3: 'trash' };

      for (const c of this.cloths) {
        const aYM = acquireYM(c);

        if (aYM && c.getStatus === 1) {
          list.push({ ...aYM, type: 'secondhand', delta: POINTS.secondhand });
        }
        // 環境友善材質事件暫時停用，待長腳衣櫥定義「環境友善材質」後恢復
        // if (aYM && c.material === 8) {
        //   list.push({ ...aYM, type: 'eco', delta: POINTS.eco });
        // }

        if (c.recycleType !== undefined && c.recycleType >= 0) {
          const evType = recycleTypeMap[c.recycleType];
          if (evType) {
            const rYM = parseYM(c.recycleTime);
            if (rYM) {
              list.push({ ...rYM, type: evType, delta: POINTS[evType] });
            }
          }
        }
      }

      for (const o of this.outfits || []) {
        if (o.available === false) continue;
        const ymd = parseYM(o.date);
        if (!ymd) continue;
        const wearCount = (o.outfitPhotos || []).filter(p => p.type === 'cloth').length;
        for (let i = 0; i < wearCount; i++) {
          list.push({ ...ymd, type: 'wear', delta: POINTS.wear });
        }
      }

      return list;
    },

    monthlyEntriesAsc() {
      const map = new Map();
      for (const e of this.events) {
        const key = `${e.year}-${String(e.month).padStart(2, '0')}`;
        if (!map.has(key)) map.set(key, { key, year: e.year, month: e.month, events: [] });
        map.get(key).events.push(e);
      }
      const arr = [...map.values()].sort((a, b) =>
        a.year !== b.year ? a.year - b.year : a.month - b.month
      );
      let running = 0;
      for (const m of arr) {
        m.delta = m.events.reduce((s, e) => s + e.delta, 0);
        running += m.delta;
        m.balance = running;

        const grouped = {};
        for (const e of m.events) {
          if (!grouped[e.type]) {
            const r = RULE_BY_TYPE[e.type];
            grouped[e.type] = { type: e.type, label: r.label, color: r.color, count: 0, delta: 0 };
          }
          grouped[e.type].count += 1;
          grouped[e.type].delta += e.delta;
        }
        m.breakdown = Object.values(grouped).sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
      }
      return arr;
    },

    monthlyEntries() {
      return [...this.monthlyEntriesAsc].reverse();
    },

    displayedEntries() {
      return this.monthlyEntries.slice(0, this.displayCount);
    },

    canShowMore() {
      return this.displayCount < this.monthlyEntries.length;
    },

    totalBalance() {
      if (this.monthlyEntriesAsc.length === 0) return 0;
      return this.monthlyEntriesAsc[this.monthlyEntriesAsc.length - 1].balance;
    },

    treesPerYear() {
      if (this.totalBalance <= 0) return 0;
      return Math.round(this.totalBalance / KG_PER_TREE_YEAR);
    },

    currentKey() {
      const now = moment();
      return `${now.year()}-${String(now.month() + 1).padStart(2, '0')}`;
    },

    thisMonthDelta() {
      const e = this.monthlyEntries.find(m => m.key === this.currentKey);
      return e ? e.delta : 0;
    },

    balanceDesc() {
      const b = this.totalBalance;
      if (b >= 442) return '累積已突破 442 kg！持續累積你的綠色衣櫥。';
      if (b >= 100) return '進度不錯，繼續記錄每次選擇。';
      if (b > 0)    return '存摺已開戶，下一步試試標記材質或記錄穿搭。';
      if (b === 0)  return '存摺等你開戶——記錄第一件衣物吧。';
      return '本月偏出帳，下次試試二手選擇或妥善處理。';
    },

    inUseCloths() {
      return this.cloths.filter(c => !c.isDeleted && c.recycleType === -1);
    },

    totalWithStatus() {
      return this.inUseCloths.filter(c => c.getStatus !== -1 && c.getStatus !== undefined).length;
    },
    secondHandCount() {
      return this.inUseCloths.filter(c => c.getStatus === 1).length;
    },
    secondHandRate() {
      if (this.totalWithStatus === 0) return 0;
      return Math.round(this.secondHandCount / this.totalWithStatus * 100);
    },

    totalWithMaterial() {
      return this.inUseCloths.filter(c => c.material !== -1 && c.material !== undefined).length;
    },
    materialKnownRate() {
      if (this.inUseCloths.length === 0) return 0;
      return Math.round(this.totalWithMaterial / this.inUseCloths.length * 100);
    },
    avgWearCount() {
      if (this.inUseCloths.length === 0) return 0;
      const total = this.inUseCloths.reduce((s, c) => s + (c.wearCount || 0), 0);
      return total / this.inUseCloths.length;
    },
  },
  methods: {
    toggle(key) {
      this.$set(this.expanded, key, !this.expanded[key]);
    },
    showMore() {
      this.displayCount = Math.min(this.monthlyEntries.length, this.displayCount + this.pageSize);
    },
    pad(n) { return String(n).padStart(2, '0'); },
    fmt(n) {
      const r = Math.round(n * 10) / 10;
      return Number.isInteger(r) ? String(r) : r.toFixed(1);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/basic";

.passbook-page {
  padding: .5rem 0 2rem;
  background: #fff;
  min-height: 100%;
}

.pos { color: $typography-interactive-primary; }
.neg { color: #DE6167; }

// ===== Hero =====
.hero {
  position: relative;
  text-align: center;
  padding: 1.5rem 1rem 1.25rem;
  margin-bottom: 1rem;

  .hero-info-btn {
    position: absolute;
    top: .5rem;
    right: .5rem;
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 0;
    color: $typography-secondary-default;
    cursor: pointer;
    border-radius: 999px;
    transition: background-color .15s ease, color .15s ease;
    &:hover {
      background-color: rgba(0, 0, 0, .05);
      color: $typography-primary-default;
    }
  }

  .hero-label {
    color: $typography-secondary-default;
    letter-spacing: .1em;
    margin-bottom: .25rem;
  }
  .hero-balance {
    line-height: 1;
    color: $typography-primary-default;
    .num { font-size: 3rem; }
    .unit {
      font-size: 1rem;
      margin-left: 6px;
      color: $typography-secondary-default;
      vertical-align: middle;
    }
  }
  .hero-equiv {
    color: $typography-secondary-default;
    margin-top: .25rem;
  }
  .hero-month {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: .75rem;
    padding: .25rem .75rem;
    background-color: rgba(125, 196, 183, .14);
    border-radius: 999px;
    font-size: 13px;
    font-weight: bold;
    &.neg { background-color: rgba(222, 97, 103, .14); }
    .ind { font-weight: bold; }
  }
  .hero-desc {
    color: $typography-secondary-default;
    margin: .5rem 0 0;
  }
}

// ===== Section heading =====
.section-title {
  margin: 0;
  color: $typography-primary-default;
}
.section-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: .75rem;

  .section-meta { color: $typography-secondary-default; }
}

// ===== Ledger =====
.ledger { margin-bottom: 2.75rem; }
.ledger-list {
  display: flex;
  flex-direction: column;
  gap: .5rem;
}

.entry {
  background-color: #fff;
  border: 1px solid $color-border;
  border-radius: .75rem;
  overflow: hidden;
}

.entry-head {
  display: flex;
  align-items: center;
  gap: .75rem;
  width: 100%;
  padding: .75rem 1rem;
  background: transparent;
  border: 0;
  text-align: left;
  cursor: pointer;

  &:hover { background-color: rgba(0, 0, 0, .02); }
}

.entry-month {
  flex: 0 0 56px;
  .ym-yr {
    color: $typography-secondary-default;
    line-height: 1.1;
  }
  .ym-mo {
    color: $typography-primary-default;
    line-height: 1.2;
  }
}

.entry-stats {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1.25rem;

  .stat-line {
    display: flex;
    align-items: baseline;
    gap: .375rem;
  }
  .stat-label { color: $typography-secondary-default; }
  .stat-balance { color: $typography-primary-default; }
  .stat-unit {
    color: $typography-secondary-default;
    margin-left: -.125rem;
  }
}

.chev {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: $typography-secondary-default;
  transition: transform .15s ease;
  flex: 0 0 24px;

  &.is-open { transform: rotate(180deg); }
}

.entry-detail {
  padding: .5rem 1rem .75rem 4.75rem;
  border-top: 1px dashed $color-border;
  display: flex;
  flex-direction: column;
  gap: .375rem;
}
.detail-row {
  display: flex;
  align-items: center;
  gap: .5rem;

  .b-dot {
    flex: 0 0 8px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }
  .b-label { flex: 1 1 auto; color: $typography-primary-default; }
  .b-count { color: $typography-secondary-default; }
  .b-delta {
    flex: 0 0 auto;
    min-width: 56px;
    text-align: right;
  }
}

.show-more {
  display: block;
  margin: .75rem auto 0;
  padding: .5rem 1rem;
  background: transparent;
  border: 1px solid $color-border;
  border-radius: 999px;
  color: $typography-primary-default;
  cursor: pointer;

  &:hover { background-color: rgba(0, 0, 0, .03); }
}

// ===== Empty =====
.empty {
  background-color: #fff;
  border: 1px solid $color-border;
  border-radius: .75rem;
  padding: 1.75rem 1.25rem;
  text-align: center;
  color: $typography-secondary-default;
  line-height: 1.6;
}

// ===== Status snapshot =====
.status-section {
  margin: 1.5rem 0 2.75rem;

  .section-title { margin-bottom: .75rem; }
}
.stat-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: .5rem;
}
.stat-card {
  background-color: #fff;
  border: 1px solid $color-border;
  border-radius: .5rem;
  padding: .75rem;
  text-align: center;

  .stat-title {
    color: $typography-secondary-default;
    margin-bottom: .25rem;
  }
  .stat-value {
    color: $typography-primary-default;
    .num { font-size: 1.5rem; font-weight: bold; }
    .unit {
      font-size: .875rem;
      color: $typography-secondary-default;
      margin-left: 2px;
    }
  }
  .stat-sub {
    color: $typography-secondary-default;
    margin-top: .25rem;
    font-size: 11px;
    line-height: 1.3;
  }
}

// ===== Rules modal =====
::v-deep .rules-modal {
  .modal-header {
    padding: 1.5rem 1.75rem 1rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .modal-title {
    margin: 0;
  }
  .modal-close-btn {
    background: transparent;
    border: 0;
    padding: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: $typography-secondary-default;
    cursor: pointer;
    border-radius: 999px;
    transition: background-color .15s ease, color .15s ease;
    &:hover {
      background-color: rgba(0, 0, 0, .05);
      color: $typography-primary-default;
    }
  }
  .modal-body {
    padding: 1rem 1.75rem 1.75rem;
  }
}
.rules-modal-body {
  display: flex;
  flex-direction: column;
  gap: .375rem;
}
.rule-row {
  display: flex;
  align-items: center;
  gap: .5rem;

  .r-dot {
    flex: 0 0 8px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }
  .r-label { flex: 1 1 auto; color: $typography-primary-default; }
  .r-delta { flex: 0 0 auto; }
}
.rule-note {
  margin: .5rem 0 0;
  color: $typography-secondary-default;
  line-height: 1.6;

  a {
    color: $typography-interactive-primary;
    text-decoration: underline;
  }
}
</style>
